import { createSelector, createStructuredSelector } from 'reselect';
import moment from 'moment';

import { getUser, getUserIsHost, getUserSlug, getUserUnreadMessagesCount } from '../../modules/session/getters';

// Net Sales
const getSelectedTime = (state) => state.betaDashboard.selectedTime;
const getSelectedEventFilter = (state) => state.betaDashboard.selectedEventFilter;

// Balances
const getAccountBalancesLoading = (state) => state.betaDashboard.balances.loading;
const getAccountBalances = (state) => state.betaDashboard.balances.data;

// Quick Links
const getQuickLinks = (state) => state.dashboard.ui.quickLinks;

// Report
const getReportLoading = (state) => state.betaDashboard.report.loading;
const getTicketsByCurrency = (state) => state.betaDashboard.report.data.cost_items_by_currency;
const getTotalsByCurrency = (state) => state.betaDashboard.report.data.totals_by_currency;
const getUserHasFreeEventsOnly = (state) => state.betaDashboard.report.data.has_free_listings_only;

export const getReportOptions = (state) => state.betaDashboard.reportOpts.options;
const getReportSelected = (state) => state.betaDashboard.reportOpts.reportSelected;
const getReportTimeRangeSelected = (state) => state.betaDashboard.reportOpts.timeRangeSelected;
const getReportCustomTimeRange = (state) => state.betaDashboard.reportOpts.customTimeRange;
const getReportSingleDate = (state) => state.betaDashboard.reportOpts.singleDate;
const getReportFocusedInput = (state) => state.betaDashboard.reportOpts.focusedInput;

const getSelectedCurrency = (state) => state.betaDashboard.selectedCurrency;

const getCustomReportDateStr = createSelector(
  [getReportCustomTimeRange],
  (customRange) => {
    return moment(customRange.startDate).format('MMM DD, YYYY') + ' - ' +
      moment(customRange.endDate).format('MMM DD, YYYY');
  }
);

export const getSelectedReportTitle = createSelector(
  [getReportOptions, getReportSelected],
  (reportOptions, reportSelected) => {
    if (reportSelected !== 'default') {
      return reportOptions.reports.filter(report => report.value === reportSelected)[0].title || '';
    }

    return '';
  }
);

export const getSelectedReportId = createSelector(
  [getReportOptions, getReportSelected],
  (reportOptions, reportSelected) => {
    if (reportSelected !== 'default') {
      return reportOptions.reports.filter(report => report.value === reportSelected)[0].value || '';
    }

    return 'default';
  }
);

const getTickOffset = createSelector(
  [getSelectedTime],
  (selectedTime) => {
    if (selectedTime === 'mtd' || selectedTime === 'ytd') {
      return 7;
    }
    else if (selectedTime === 'wtd') {
      return 1;
    }

    return 1;
  }
);

export const getDateFilter = createSelector(
  [getSelectedTime],
  (selectedTime) => {
    const defaultParams = {
      from: moment().subtract(30, 'days').format('YYYY-MM-DD'),
      to: moment().format('YYYY-MM-DD')
    };

    if (selectedTime === 'mtd') {
      return defaultParams;
    }
    else if (selectedTime === 'wtd') {
      return {
        from: moment().subtract(1, 'week').format('YYYY-MM-DD'),
        to: moment().format('YYYY-MM-DD')
      };
    }

    return defaultParams;
  }
);

// ['Jan 1', 'Jan 8', 'Jan 15', 'Jan 22'];
const getNetSalesTicks = createSelector(
  [getSelectedTime, getTickOffset],
  (selectedTime, tickOffset) => {
    let currentDay = moment().subtract(30, 'days');

    if (selectedTime === 'mtd') {
      currentDay = moment().subtract(30, 'days');
    }
    else if (selectedTime === 'wtd') {
      currentDay = moment().subtract(1, 'week');
    }

    const ticks = [];

    while (currentDay.isBefore(moment())) {
      ticks.push(currentDay.format('MMM D'));
      currentDay = currentDay.add(tickOffset, 'days');
    }

    return ticks;
  }
);

export const getRevenueReport = createSelector(
  [getTotalsByCurrency, getSelectedCurrency],
  (totalsByCurrency, selectedCurrency) => {
    if (totalsByCurrency && totalsByCurrency.length > 0 && selectedCurrency !== '') {
      return totalsByCurrency.filter(total => total.currency === selectedCurrency)[0].fulfillment_amount || 0;
    }

    return 0;
  }
);

export const getNumTicketsSoldReport = createSelector(
  [getTicketsByCurrency, getSelectedCurrency],
  (ticketsByCurrency, selectedCurrency) => {
    if (ticketsByCurrency && ticketsByCurrency.length > 0 && selectedCurrency !== '') {
      return ticketsByCurrency.filter(tickets => tickets.currency === selectedCurrency)[0].cost_items_sold || 0;
    }

    return 0;
  }
);

export const getUserCurrencies = createSelector(
  [getUser],
  (user) => {
    if (user && user.currencies.length > 0) {
      return user.currencies;
    }

    return [];
  }
);

export default createStructuredSelector({
  accountBalancesLoading: getAccountBalancesLoading,
  balances: getAccountBalances,
  currencies: getUserCurrencies,
  customDateStr: getCustomReportDateStr,
  dateFilter: getDateFilter,
  netSalesTicks: getNetSalesTicks,
  numTicketsSold: getNumTicketsSoldReport,
  reportOptions: getReportOptions,
  reportSelected: getReportSelected,
  reportTimeRangeSelected: getReportTimeRangeSelected,
  reportCustomTimeRange: getReportCustomTimeRange,
  reportFocusedInput: getReportFocusedInput,
  reportSingleDate: getReportSingleDate,
  selectedCurrency: getSelectedCurrency,
  selectedEventFilter: getSelectedEventFilter,
  selectedReportId: getSelectedReportId,
  selectedReportTitle: getSelectedReportTitle,
  selectedTime: getSelectedTime,
  quickLinks: getQuickLinks,
  revenue: getRevenueReport,
  reportLoading: getReportLoading,
  user: getUser,
  userHasFreeEventsOnly: getUserHasFreeEventsOnly,
  userIsHost: getUserIsHost,
  userSlug: getUserSlug,
  userUnreadMessagesCount: getUserUnreadMessagesCount

});



// WEBPACK FOOTER //
// ./src/modules/beta-dashboard/getters.js